<script>
  import { format } from 'svelte-i18n';
  import { Offcanvas } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { PitchChart } from '@client/components/charts/pitch';
  import { Icon } from '@client/components/icon';
  import { Recipient } from '@client/components/recipient';

  import { appStore } from '@client/stores/app';

  import { getFlag } from '@client/utils/flags';
  import { resizeIframe } from '@client/utils/iframe';

  import { COLORS } from '@client/enums';

  import { durationFromSeconds } from '@common/utils/dates';

  export let data = {};
  export let hash = '';

  let fusionUrl = '';
  let activeFusionReplay = '';
  let activeFusionReplayConfig = {};

  const changeFusionReplay = (url, config) => {
    if (activeFusionReplay === url) {
      activeFusionReplay = '';
      fusionUrl = '';
      activeFusionReplayConfig = {};
      return;
    }

    activeFusionReplayConfig = config;
    fusionUrl = url;
    activeFusionReplay = fusionUrl;
  };

  const getChartData = (sessions) => {
    const labels = [];

    const datasets = sessions
      .sort((a, b) => new Date(b.utcts) - new Date(a.utcts))
      .flatMap((session) => {
        // FUTURE Kieran: You'll probably need to add :ss back for uniqueness
        const label = formatDate(new Date(session.utcts), 'M/dd/yy h:mm a');
        const series = session.series;

        return series.map((v, index) => ({
          data: [
            {
              y: label,
              label: `${$format('label.SLIDE')} ${index + 1}`,
              x: v / 1000 || 0.1
            }
          ],
          backgroundColor: COLORS[index],
          label: `${$format('label.SLIDE')} ${index + 1}`
        }));
      });

    return {
      animation: {
        duration: 10000
      },
      hash,
      datasets,
      labels
    };
  };

  const generatePitchRecipientSessions = (sessions) => {
    const ids = Object.keys(sessions);
    const grouped = Object.values(sessions).reduce((acc, config, index) => {
      const sessionId = ids[index].replace('-id', '');

      if (!acc[config.recipient]) {
        acc[config.recipient] = [];
      }

      acc[config.recipient].push({
        ...config,
        sessionId,
        replayUrl: `https://www.sendergen.com/docsession/${config.msghashid}/${sessionId}/watch.html`
      });

      return acc;
    }, {});

    const sorted = Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key];
        return acc;
      }, {});

    return Object.entries(sorted);
  };

  const groupByDates = (data) => {
    const groups = data
      .sort((a, b) => new Date(b.utcts) - new Date(a.utcts))
      .reduce((acc, config) => {
        const date = formatDate(new Date(config.utcts), 'M/d/yyyy');

        config.duration = durationFromSeconds(config.series.reduce((sum, value) => sum + value, 0) / 1000);
        config.slidesviewed = config.series.filter(Boolean).length;

        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push({ ...config });

        return acc;
      }, {});

    return Object.entries(groups);
  };
</script>

<div class="slide-analytics">
  {#if Object.entries(data)?.length}
    <div class="sessions">
      {#each generatePitchRecipientSessions(data) as [recipient, sessions]}
        <div class="recipient-history">
          <Recipient {recipient} history={[]} />
          <div class="pitch-chart">
            <PitchChart data={getChartData(sessions, recipient)} />
          </div>
          {#each groupByDates(sessions) as [date, records]}
            <div class="day-block">
              <div class="date">{formatDate(new Date(date), 'EEEE, MMM d, yyyy')}</div>
              <div class="timeline">
                {#each records as record}
                  <div
                    class="item"
                    role="button"
                    tabindex="0"
                    on:click={() => changeFusionReplay(record.replayUrl, record)}
                    class:active={activeFusionReplay === record.replayUrl}
                  >
                    <div class="icon">
                      <i
                        class:os-icon-play-circle={activeFusionReplay !== record.replayUrl}
                        class:os-icon-pause-circle={activeFusionReplay === record.replayUrl}
                      />
                    </div>
                    <div class="time">
                      <div>{formatDate(new Date(record.utcts), 'h:mm:ss aa')}</div>
                      <small
                        >{record.duration} - {record.slidesviewed}
                        {record.slidesviewed > 1
                          ? $format('label.PAGES').toLowerCase()
                          : $format('label.PAGE').toLowerCase()}</small
                      >
                    </div>

                    <div class="details">
                      <div>
                        <span class={getFlag(record.note.location)} />
                        {record.note.location}
                      </div>
                      <small>
                        {$format('label.ISP_IP', { values: { isp: record.note.isp, ip: record.ip } })}
                      </small>
                      <a href={record.replayUrl} target="_blank" class="external-link">
                        <Icon name="external-link" />
                      </a>
                    </div>
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      {/each}
    </div>
  {/if}

  {#if $appStore.mobile}
    <Offcanvas isOpen={activeFusionReplay} toggle={() => changeFusionReplay()} placement="end" style="width: 98%">
      <div slot="header" class="offcanvas-heading">
        <Icon name="video" />
        {#if activeFusionReplay}
          <span>{formatDate(new Date(activeFusionReplayConfig.utcts), 'EEEE, MMM d, yyyy @ h:mm:ss aa')}</span>
        {/if}
      </div>
      <div class="offcanvas-playback">
        {#if activeFusionReplay}
          <div class="details">
            <div>
              <span class={getFlag(activeFusionReplayConfig.note.location)} />
              {activeFusionReplayConfig.note.location}
            </div>
            <small>
              {$format('label.ISP_IP', {
                values: { isp: activeFusionReplayConfig.note.isp, ip: activeFusionReplayConfig.ip }
              })}
            </small>
          </div>
        {/if}
        {#if fusionUrl}
          {#key fusionUrl}
            <iframe tabindex="-1" style="visibility: hidden;" title="preview" on:load={resizeIframe} src={fusionUrl} />
          {/key}
        {:else}
          <div class="empty-player">{$format('label.SLIDE_ANALYTICS_SELECT_SESSION')}</div>
        {/if}
      </div>
    </Offcanvas>
  {:else}
    <div class="iframe">
      {#if fusionUrl}
        {#key fusionUrl}
          <iframe tabindex="-1" style="visibility: hidden;" title="preview" on:load={resizeIframe} src={fusionUrl} />
        {/key}
      {:else}
        <div class="empty-player">{$format('label.SLIDE_ANALYTICS_SELECT_SESSION')}</div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss" src="./slide-analytics.scss"></style>
